/**
 * Login page — the only unauthenticated route in the dashboard.
 *
 * Shown when `AuthGuard` (see `lib/auth.tsx`) determines the user has
 * no valid session cookie, either because they haven't logged in yet
 * or because their 24-hour session expired.
 *
 * The page is deliberately minimal:
 *
 * - One "Sign in with Google" link that points at
 *   `/auth/google/login`, which is handled by the backend
 *   (`src/server/routes/auth_google.rs::login`). That handler
 *   generates the PKCE + CSRF state cookie and 302-redirects the
 *   browser to Google's OAuth endpoint.
 * - Error messages read from the `?error=...` query param. The
 *   callback handler (`src/server/routes/auth_google.rs::callback`)
 *   redirects here with error codes on any of the failure modes —
 *   we map each to a human-friendly message, falling through to a
 *   generic "Login error" for unknown codes so future additions
 *   don't silently show a blank red box.
 * - No "remember me" checkbox, no alternative auth methods, no
 *   "forgot password" link. This is internal, Google-SSO-only. Keep
 *   the surface tight.
 * - The subtitle's environment label (`prod` / `staging`) comes from
 *   the public `GET /api/env` (step 0.1) via a raw `fetch` — see the
 *   comment in `LoginPage` for why `api()` must not be used here.
 *
 * The link is a plain `<a>` — not a `<form>` or React-Router
 * `<Link>` — because `/auth/google/login` is a full-page redirect
 * outside the SPA's routing scope. React Router's `<Link>` would
 * prevent the default navigation, breaking the flow.
 */

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import type { PublicEnv } from "@/lib/api";

/**
 * Map backend error codes (appended to `/login?error=XXX` by the
 * OAuth callback handler on failure) to user-facing messages.
 *
 * Keys must match the exact strings emitted by
 * `src/server/routes/auth_google.rs::callback` on each failure path.
 * Adding a new error path on the backend requires adding an entry
 * here — fallthrough shows the raw code, which is visible enough
 * that we'll notice and fix it, but ugly enough that we want to
 * avoid in production.
 */
const ERROR_MESSAGES: Record<string, string> = {
  expired: "Your login session expired. Please try again.",
  invalid_state: "Invalid login state. Please try again.",
  csrf_mismatch: "Security check failed. Please try again.",
  token_exchange:
    "Failed to authenticate with Google. Please try again in a moment.",
  token_parse: "Failed to process Google's response. Please try again.",
  domain_restricted:
    "Access is restricted to authorized Google Workspace accounts. Sign in with your cantina.ai account.",
  email_not_verified:
    "Your Google email address isn't verified. Verify it with Google and try again.",
  no_email:
    "Google didn't return an email address for your account. Please try again.",
};

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const errorMessage = errorCode
    ? (ERROR_MESSAGES[errorCode] ?? `Login error: ${errorCode}`)
    : null;

  // Environment label for the subtitle. Raw `fetch`, NOT `api()`:
  // `api()` full-page-redirects to Google on any 401 (lib/api.ts), which
  // on the one unauthenticated page would auto-start OAuth (and loop on
  // OAuth errors). `/api/env` is public (step 0.1); any failure just
  // leaves the subtitle neutral — the label is cosmetic.
  const [environment, setEnvironment] = useState<string | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch("/api/env", { credentials: "same-origin", signal: ctrl.signal })
      .then((res) => (res.ok ? (res.json() as Promise<PublicEnv>) : null))
      .then((body) => {
        if (body && typeof body.environment === "string" && body.environment) {
          setEnvironment(body.environment);
        }
      })
      .catch(() => {
        /* cosmetic — keep the neutral subtitle */
      });
    return () => ctrl.abort();
  }, []);

  // Deep-link preservation: if the SPA's AuthGuard (or the api()
  // 401 handler) forwarded the user here with a `?next=<path>`
  // param naming the original URL they tried to reach, forward
  // that value through to `/auth/google/login?next=<path>`. The
  // backend validates the path in `sanitize_next`
  // (src/server/routes/auth_google.rs) — this client-side
  // forwarding carries no security weight on its own; the server
  // is the authoritative gate.
  //
  // When `next` is absent, the sign-in link stays at the plain
  // `/auth/google/login` shape and the callback falls back to
  // redirecting to `/`, matching the pre-deep-link-feature
  // behavior.
  const nextParam = searchParams.get("next");
  const signInHref =
    nextParam && nextParam.length > 0
      ? `/auth/google/login?next=${encodeURIComponent(nextParam)}`
      : "/auth/google/login";

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
      <div className="w-full max-w-sm space-y-6 rounded-lg border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {/* Header */}
        <div className="space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
            triage
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {environment
              ? `Sign in to the ${environment} dashboard`
              : "Sign in to the dashboard"}
          </p>
        </div>

        {/* Error message (only rendered when ?error=... is present) */}
        {errorMessage && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200"
          >
            {errorMessage}
          </div>
        )}

        {/*
          Google sign-in button.
          Plain <a> (not <Link>) — `/auth/google/login` is a backend
          route, not an SPA route. Using React Router's <Link> would
          prevent the default browser navigation and break the flow.
        */}
        <a
          href={signInHref}
          className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50 dark:hover:bg-gray-700"
        >
          {/*
            Google "G" logo as inline SVG. Standard four-color
            rendering per Google's branding guidelines. Inline rather
            than imported so the bundle doesn't pull in an icon
            library for a single glyph.
          */}
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </a>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 dark:text-gray-400">
          Restricted to cantina.ai workspace accounts
        </p>
      </div>
    </div>
  );
}
